import {useCallback, useLayoutEffect, useRef} from 'react';

/**
 * Returns a stable callback that always invokes the latest `fn`, or
 * `undefined` when no `fn` was provided. Lets effects depend on a
 * callback without re-subscribing when callers pass inline functions.
 *
 * This is a userspace approximation of React 19's useEffectEvent. The
 * latest-ref technique is documented in the legacy React Hooks FAQ:
 * https://legacy.reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
 * Revisit this hook when we upgrade to React 19.
 */
export const useEventCallback = <Args extends unknown[], Return>(
  fn: ((...args: Args) => Return) | undefined
): ((...args: Args) => Return | undefined) | undefined => {
  const ref = useRef(fn);

  useLayoutEffect(() => {
    ref.current = fn;
  }, [fn]);

  const stable = useCallback((...args: Args) => ref.current?.(...args), []);

  return fn === undefined ? undefined : stable;
};
